// WorkflowActionsDrawer — the report view's Workflow actions rail pill opens
// this: the submission's CURRENT workflow step (GET
// submissions/workflow-step-details/:id, fetched on first open) as a header
// meta line (Step position/total · step title · form name) and one card per
// response element the reviewer can pick. Clicking a card fires the transition
// (PATCH submissions/update-step/:submissionId, body {stepId, elementId}) —
// success closes the drawer, and the mutation's invalidation refetches the
// step and both report renders through the shared per-submission tag.
//
// The routes-to line is derived from the element's actionType (the builder's
// own WorkflowElementActionType vocabulary): back_to_user and end_workflow are
// fixed copy; next_step reads the populated `nextStep` — whose confirmed shape
// is still pending a real sample, so that branch renders only what it can
// prove (see SubmissionWorkflowNextStep).

import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Drawer, Icon } from '@/components/common';
import { WorkflowElementActionType } from '@/api/endpoints/workflowsApi';
import {
  SubmissionFlowState,
  workflowFlowStateOf,
  useGetSubmissionWorkflowStepQuery,
  useUpdateSubmissionStepMutation,
  type SubmissionWorkflowElement,
} from '@/api/endpoints/submissionsApi';
import type { ApiErrorShape } from '@/api/types';

interface WorkflowActionsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** The submission whose current step is shown. */
  submissionId: string;
  /** The form's name — the meta line's trailing segment (from route state). */
  formName?: string;
}

/** Card tile tone + glyph per routing action. Exhaustive — the enum is the
 *  builder's confirmed three-value vocabulary, so there is no fallback branch. */
const ACTION_META: Record<WorkflowElementActionType, { tone: 'success' | 'warning'; icon: string }> =
  {
    [WorkflowElementActionType.EndWorkflow]: { tone: 'success', icon: 'circle-check' },
    [WorkflowElementActionType.NextStep]: { tone: 'success', icon: 'circle-check' },
    [WorkflowElementActionType.BackToUser]: { tone: 'warning', icon: 'corner-up-left' },
  };

const WorkflowActionsDrawer = ({
  open,
  onClose,
  submissionId,
  formName,
}: WorkflowActionsDrawerProps) => {
  const { t } = useTranslation();

  const { data, isFetching, isError, error } = useGetSubmissionWorkflowStepQuery(submissionId, {
    skip: !open,
  });

  // A success:false answer here is a STATE, not a failure — the notice card
  // below renders every rejection: flowState 'end' (workflow complete), 'user'
  // (back with the filler), and otherwise "not allowed" — the backend sends NO
  // meaningful flowState for that case (null or absent, not a real enum
  // value), so the check is simply !flowState.
  const rejection = isError ? (error as ApiErrorShape) : undefined;
  const flowState = workflowFlowStateOf(rejection);
  const notice = !rejection
    ? undefined
    : flowState === SubmissionFlowState.End
      ? {
          icon: 'lock',
          tone: 'success' as const,
          title: t('report_workflow_ended_title'),
          text: t('report_workflow_ended_body'),
        }
      : flowState === SubmissionFlowState.User
        ? {
            icon: 'corner-up-left',
            tone: 'warning' as const,
            title: t('report_workflow_returned_title'),
            text: t('report_workflow_returned_body'),
          }
        : {
            icon: 'ban',
            tone: 'warning' as const,
            title: t('report_workflow_not_allowed_title'),
            // Fixed localized copy, not rejection.msg — the wire message is
            // English-only.
            text: t('report_workflow_not_allowed_body'),
          };

  // One transition at a time — every card disables while one is in flight. A
  // workflow rejection can carry a server message worth showing (the
  // downloadPdf idiom); anything else falls through to the generic copy.
  const [updateStep, { isLoading: submitting }] = useUpdateSubmissionStepMutation();
  const runAction = async (element: SubmissionWorkflowElement) => {
    if (!data) return;
    try {
      await updateStep({ submissionId, stepId: data.id, elementId: element.id }).unwrap();
      toast.success(t('report_workflow_done'));
      onClose();
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  // The bold half of the "Routes to …" line. next_step renders only the fields
  // the (still unconfirmed) populated nextStep actually carries.
  const routesTo = (element: SubmissionWorkflowElement): string => {
    switch (element.actionType) {
      case WorkflowElementActionType.BackToUser:
        return t('report_workflow_route_filler');
      case WorkflowElementActionType.EndWorkflow:
        return t('report_workflow_route_end');
      case WorkflowElementActionType.NextStep:
        return [
          element.nextStep?.position != null
            ? t('report_workflow_route_step', { n: element.nextStep.position })
            : undefined,
          element.nextStep?.title,
        ]
          .filter(Boolean)
          .join(' · ');
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      className='workflow-drawer'
      icon='workflow'
      title={t('report_view_workflow')}
      subtitle={
        data
          ? t('report_workflow_meta', {
              position: data.position,
              total: data.totalSteps,
              step: data.title,
              form: formName ?? '',
            })
          : // No step payload (loading, error, or an ended workflow — that
            // rejection carries no step data): the form name keeps the header
            // anchored to what the drawer is about.
            formName
      }
    >
      {isFetching && <p className='workflow-drawer__status'>{t('text_loading')}</p>}

      {notice && !isFetching && (
        <div className='workflow-drawer__notice'>
          <span
            className={`workflow-drawer__tile workflow-drawer__tile--${notice.tone}`}
            aria-hidden='true'
          >
            <Icon name={notice.icon} size={18} />
          </span>
          <div className='workflow-drawer__notice-body'>
            <span className='workflow-drawer__notice-title'>{notice.title}</span>
            <p className='workflow-drawer__notice-text'>{notice.text}</p>
          </div>
        </div>
      )}

      {data && !isFetching && !isError && (
        <>
          <h3 className='workflow-drawer__label'>{t('report_workflow_response_label')}</h3>

          {data.elements.map((element) => {
            const meta = ACTION_META[element.actionType];
            return (
              <button
                key={element.id}
                type='button'
                className='workflow-drawer__card'
                disabled={submitting}
                onClick={() => runAction(element)}
              >
                <span
                  className={`workflow-drawer__tile workflow-drawer__tile--${meta.tone}`}
                  aria-hidden='true'
                >
                  <Icon name={meta.icon} size={18} />
                </span>
                <span className='workflow-drawer__card-body'>
                  <span className='workflow-drawer__card-title'>{element.title}</span>
                  <span className='workflow-drawer__card-route'>
                    <Icon name='corner-down-right' size={13} />
                    {t('report_workflow_routes_to')}
                    <strong>{routesTo(element)}</strong>
                  </span>
                </span>
                <Icon className='workflow-drawer__chevron' name='chevron-right' size={16} />
              </button>
            );
          })}

          <p className='workflow-drawer__note'>
            <Icon name='workflow' size={14} />
            {t('report_workflow_configured')}
          </p>
        </>
      )}
    </Drawer>
  );
};

export default WorkflowActionsDrawer;
